import type { Project } from '../types/tauri';
import type { IBackend, TerminalSessionData } from '../services/backend/IBackend';
import { useProjectStore } from '../stores/projectStore';
import { useTerminalStore } from '../stores/terminalStore';
import { useTerminalLayoutStore } from '../stores/terminalLayoutStore';
import { isGenericDaemonTitle, agentDisplayName, getNextTitle } from './terminalTitles';
import { isWeb } from './platform';
import { logger } from './logger';

const TAG = 'sessionAdopt';

/** Resolve a surviving daemon session to a known project. Id-first (the
 *  ATELIER_PROJECT_ID stamp is authoritative and survives folder moves), then
 *  cwd/path fallback for sessions created before stamping existed. */
export function resolveSessionProject(
  s: TerminalSessionData,
  projects: Project[],
): Project | null {
  if (s.projectId) {
    const byId = projects.find((p) => p.id === s.projectId);
    if (byId) return byId;
  }
  return projects.find((p) => p.root_path === s.projectPath) ?? null;
}

/**
 * Startup / reconnect workspace restore (desktop only).
 *
 * The pty-daemon keeps every terminal PTY alive across an app close+reopen or
 * reload, but the UI's terminalStore starts empty — so until the user manually
 * opens each project, that project's surviving sessions are invisible and the
 * project shows "Inactive". This seeds terminalStore with EVERY surviving
 * session, grouped by its project, so:
 *   - every project that has live terminals immediately shows Active in the
 *     list / sidebar / Home (isProjectActive derives purely from the store), and
 *   - its tabs are pre-staged, so opening the project shows them instantly.
 *
 * The per-project TerminalTab sync then reconciles by PTY id (so this never
 * creates duplicates) and re-attaches the PTY when the project is opened.
 *
 * Safety contract:
 *   - Adopt-only — NEVER spawns a PTY (so it's inert on the web/PWA viewer too,
 *     though we skip there anyway; see below).
 *   - Idempotent — dedupes by PTY id, so it's safe to call on every reconnect.
 *   - On a failed listSessions (IPC mid-connect) or an unloaded project list it
 *     no-ops and returns false so the caller can retry; it must NEVER treat a
 *     thrown/empty list as "there are no sessions" (the 2026-06-12 reload
 *     incident: acting on a mid-reconnect empty list orphans live PTYs).
 *
 * Returns true once it has run against a real session list (even if 0 adopted),
 * false if it bailed early and should be retried.
 */
export async function adoptAllDaemonSessions(backend: IBackend): Promise<boolean> {
  // Remote/PWA clients intentionally keep a PARTIAL terminalStore (only the
  // projects this device opened); the mobile switcher derives activeness
  // straight from listSessions there. Seeding the store globally would fight
  // that model, so skip — this restore is for the desktop host.
  if (isWeb()) return false;

  const projects = useProjectStore.getState().projects;
  if (projects.length === 0) return false; // project list not loaded yet — retry

  let sessions: TerminalSessionData[];
  try {
    sessions = await backend.terminal.listSessions();
  } catch (err) {
    logger.debug(TAG, 'listSessions failed (IPC not ready?) — will retry', err);
    return false;
  }

  let seeded = 0;
  for (const s of sessions) {
    if (s.status !== 'active') continue;
    const proj = resolveSessionProject(s, projects);
    if (!proj) continue; // true orphan (project not registered) — leave to AllSessionsModal

    const alreadyTabbed = useTerminalStore.getState().sessions.has(s.id);
    // Use the existing tab's title if we already have it (snapshot-restored or
    // TerminalTab-adopted); otherwise derive it from the agent identity.
    const existing = useTerminalStore.getState().sessions.get(s.id);
    const agentName = agentDisplayName(s.agentType);
    const title =
      existing?.title ??
      (agentName && isGenericDaemonTitle(s.title)
        ? getNextTitle(agentName, useTerminalStore.getState().getProjectSessions(proj.root_path))
        : s.title);

    // Record EVERY resolvable live session into the durable layout — regardless
    // of whether it's already tabbed — so a project is restorable after a reboot
    // even if the user never opens it this run. (This must run BEFORE the
    // already-tabbed short-circuit below, or snapshot-restored sessions — the
    // majority on a normal reload — would never get recorded.) Idempotent
    // (keyed by PTY id), never prunes.
    useTerminalLayoutStore.getState().upsertLiving(proj.root_path, {
      ptyId: s.id,
      title,
      isAgent: s.isAgent ?? false,
      agentType: s.agentType,
      kind: s.kind,
      launchCmd: s.launchCmd,
      runConfig: s.runConfig,
    });

    // Terminal-store seeding is adopt-only and dedupes by PTY id — a session
    // already tabbed is left untouched here.
    if (alreadyTabbed) continue;

    useTerminalStore.getState().addSession(s.id, title, proj.root_path, s.isAgent ?? false);

    // Re-bind the Run button to an adopted still-running run-config session so
    // "running" state + Stop survive the restart (mirrors the per-project sync).
    if (s.kind === 'run' && useTerminalStore.getState().runSessionIds.get(proj.root_path) == null) {
      useTerminalStore.getState().setRunSession(proj.root_path, s.id);
    }
    seeded++;
  }

  if (seeded > 0) {
    logger.info(TAG, `Adopted ${seeded} surviving terminal session(s) across projects`);
  }
  return true;
}
